/* src/components/auth/PasswordReset.js */
import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebaseConfig'; // Adjust path if needed
// Import the loading context
import { useLoading } from '../../contexts/LoadingContext';

// Helper function for user-friendly error messages during password reset
const getResetErrorMessage = (errorCode) => {
    switch (errorCode) {
        case 'auth/invalid-email':
            return 'Please enter a valid email address.';
        case 'auth/user-not-found':
            // Avoid confirming if an email exists for security, use a generic message.
            return 'If an account exists for this email, a password reset link has been sent.';
        case 'auth/missing-email':
            return 'Please enter your email address.';
        case 'auth/network-request-failed':
            return 'Network error. Please check your connection and try again.';
        default:
            console.error("Unhandled Firebase Reset Error Code:", errorCode);
            return 'An error occurred while trying to send the reset email. Please try again.';
    }
};

const PasswordReset = ({ onSwitchToLogin }) => { // Accept prop to switch back to login
    // --- State ---
    const [email, setEmail] = useState('');
    const [error, setError] = useState(null);       // Stores error messages
    const [message, setMessage] = useState(null);   // Stores success/info messages
    
    // Use the loading context instead of local loading state
    const { showLoading, hideLoading } = useLoading();

    // --- Handlers ---

    /** Handles the password reset request */
    const handleResetRequest = async (e) => {
        e.preventDefault(); // Prevent default form submission
        if (!email) return; // Prevent submission without email

        // Show loading overlay
        showLoading("Sending reset link...");
        
        // Clear previous messages
        setError(null);
        setMessage(null);

        try {
            console.log(`Attempting password reset for: ${email}`);
            await sendPasswordResetEmail(auth, email);
            // Show a generic success message regardless of whether the user exists, for security.
            setMessage('If an account exists for this email, a password reset link has been sent. Please check your inbox (and spam folder).');
            console.log(`Password reset email potentially sent to: ${email}`);
            // Hide loading on success
            hideLoading();
        } catch (firebaseError) {
            console.error("Firebase Password Reset Error:", firebaseError.code, firebaseError.message);
            const friendlyMessage = getResetErrorMessage(firebaseError.code);
            // Special case: If user-not-found, still show the generic success message.
            if (firebaseError.code === 'auth/user-not-found') {
                setMessage('If an account exists for this email, a password reset link has been sent. Please check your inbox (and spam folder).');
            } else {
                setError(friendlyMessage);
            }
            // Hide loading on error
            hideLoading();
        }
    };

    // --- Function to switch back to Login view ---
    const navigateToLogin = () => {
        if (onSwitchToLogin && typeof onSwitchToLogin === 'function') {
            onSwitchToLogin(); // Call the function passed from the parent (App.js or AuthContainer)
        } else {
            console.log("Navigate to Login Screen (Placeholder - implement via props)");
            alert("Switching to login not implemented via props.");
        }
    };

    // --- Render ---
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
                <h1 className="text-2xl font-bold text-center mb-4 text-gray-800">
                    Reset Password
                </h1>
                <p className="text-center text-sm text-gray-600 mb-6">
                    Enter your email address below, and we'll send you a link to reset your password.
                </p>

                {/* Reset Form */}
                <form onSubmit={handleResetRequest}>
                    {/* Email Input */}
                    <div className="mb-4">
                        <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                        </label>
                        <input
                            type="email"
                            id="reset-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="you@example.com"
                            aria-label="Email Address"
                        />
                    </div>

                    {/* Error Message Display */}
                    {error && (
                        <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200" role="alert">
                            {error}
                        </p>
                    )}

                    {/* Success/Info Message Display */}
                    {message && (
                        <p className="mb-4 text-sm text-green-600 bg-green-50 p-3 rounded border border-green-200" role="status">
                            {message}
                        </p>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={!email} // Disable if email is empty
                        className="w-full mt-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Send Reset Link
                    </button>
                </form>

                {/* Link back to Login */}
                <div className="mt-6 text-sm text-center">
                    <button
                        type="button"
                        onClick={navigateToLogin}
                        className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PasswordReset;